import React, { useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  viewerMutationOptions,
  viewerQueryKeys,
  viewerQueryOptions,
  type PanoramaHotspot,
  type PanoramaPhoto,
} from '@ironview/interactive-viewer';
import {
  getHotspotOverlays,
  getProjectUsers,
} from '../api/api';
import {
  Photo360Response,
  PhotoHotspotsResponse,
  HotspotOverlaysResponse,
  ViewPosition,
  CreateHotspotResponse,
  ProjectUser,
  HotspotOverlay,
} from '../types/types';
import { invalidateLayoutHotspots } from './useLayoutHotspots';
import {
  createIronview360ViewerContext,
  ironview360ViewerAdapter,
} from '../adapters/ironview360DataAdapter';
import {
  fromViewerPanoramaHotspot,
  fromViewerPanoramaPhoto,
} from '../adapters/ironview360Adapter';
import {
  cancelIronview360PhotoHotspots,
  getIronview360PhotoHotspotQueries,
  invalidateIronview360PhotoHotspots,
  setIronview360PhotoHotspotQueries,
} from '../adapters/ironview360ViewerCache';

export const usePhoto360 = (
  photoId: number | undefined,
  projectId?: string,
) => {
  const context = createIronview360ViewerContext(projectId ?? '');
  return useQuery<PanoramaPhoto, Error, Photo360Response>({
    ...viewerQueryOptions.panoramaPhoto(
      ironview360ViewerAdapter,
      context,
      photoId ?? '',
    ),
    enabled: photoId !== undefined && !!projectId,
    select: fromViewerPanoramaPhoto,
  });
};

export const usePhotoHotspots = (
  photoId: number | undefined,
  projectId?: string,
) => {
  const context = createIronview360ViewerContext(projectId ?? '');
  return useQuery<
    readonly PanoramaHotspot[],
    Error,
    PhotoHotspotsResponse
  >({
    ...viewerQueryOptions.photoHotspots(
      ironview360ViewerAdapter,
      context,
      photoId ?? '',
    ),
    enabled: photoId !== undefined && !!projectId,
    select: (hotspots) => ({
      hotspots: hotspots.map(fromViewerPanoramaHotspot),
    }),
    staleTime: 0,
    gcTime: 1000 * 60 * 5,
    refetchOnMount: 'always',
    refetchOnReconnect: 'always',
    retry: 3,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  });
};

export const usePhotoData = (
  photoId: number | undefined,
  projectId?: string,
) => {
  const photo360Query = usePhoto360(photoId, projectId);
  const hotspotQuery = usePhotoHotspots(photoId, projectId);

  return {
    photoData: photo360Query.data,
    photo360Data: photo360Query.data,
    hotspotsData: hotspotQuery.data,
    isLoading: photo360Query.isLoading || hotspotQuery.isLoading,
    error: photo360Query.error || hotspotQuery.error,
    refetchPhoto: photo360Query.refetch,
    refetchHotspots: hotspotQuery.refetch,
  };
};

export const useCreateHotspot = (projectId?: string) => {
  const queryClient = useQueryClient();

  return useMutation<
    CreateHotspotResponse,
    Error,
    { photoId: number; position: ViewPosition },
    { previousHotspots?: readonly PanoramaHotspot[] }
  >({
    mutationFn: async ({ photoId, position }) => {
      if (!projectId) {
        throw new Error('Project ID is required for creating hotspots');
      }
      const hotspot = await viewerMutationOptions
        .createHotspot(ironview360ViewerAdapter)
        .mutationFn({
          context: createIronview360ViewerContext(projectId),
          photoId,
          position,
        });
      return {
        id: Number(hotspot.id),
        pitch: hotspot.pitch,
        yaw: hotspot.yaw,
        hfov: hotspot.hfov,
        title: hotspot.title ?? undefined,
        created_at: hotspot.createdAt ?? new Date().toISOString(),
      };
    },
    onMutate: async ({ photoId, position }) => {
      if (!projectId) return {};
      const queryKey = viewerQueryKeys.photoHotspots(
        ironview360ViewerAdapter.id,
        projectId,
        photoId,
      );
      await queryClient.cancelQueries({ queryKey });
      const previousHotspots =
        queryClient.getQueryData<readonly PanoramaHotspot[]>(queryKey);

      const optimisticHotspot: PanoramaHotspot = {
        id: Date.now(),
        photoId,
        pitch: position.pitch,
        yaw: position.yaw,
        hfov: position.hfov,
        objects: [],
        createdAt: new Date().toISOString(),
        metadata: {
          overlayCount: 0,
          createdBy: 'current_user',
        },
      };

      queryClient.setQueryData<readonly PanoramaHotspot[]>(
        queryKey,
        (old) => [...(old ?? []), optimisticHotspot],
      );

      return { previousHotspots };
    },
    onError: (err, variables, context) => {
      console.error('Error creating hotspot:', err);
      if (projectId && context?.previousHotspots !== undefined) {
        queryClient.setQueryData(
          viewerQueryKeys.photoHotspots(
            ironview360ViewerAdapter.id,
            projectId,
            variables.photoId,
          ),
          context.previousHotspots,
        );
      }
    },
    onSettled: async (_data, error, variables) => {
      if (error) console.error('Error creating hotspot:', error);
      await invalidateIronview360PhotoHotspots(
        queryClient,
        variables.photoId,
      );
      invalidateLayoutHotspots(queryClient);
    },
  });
};

export const useUpdateHotspot = (projectId?: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ hotspotId, data }: { hotspotId: number; data: { title?: string; pitch?: number; yaw?: number; hfov?: number } }) => {
      if (!projectId) throw new Error('Project ID is required for updating hotspots');
      return viewerMutationOptions
        .updateHotspot(ironview360ViewerAdapter)
        .mutationFn({
          context: createIronview360ViewerContext(projectId),
          hotspotId,
          changes: data,
        });
    },
    onMutate: async ({ hotspotId, data }) => {
      await queryClient.cancelQueries({ queryKey: ['hotspotObjects', hotspotId] });
      if (projectId) {
        await cancelIronview360PhotoHotspots(queryClient, projectId);
      }
      const previousPhotoHotspots = projectId
        ? getIronview360PhotoHotspotQueries(queryClient, projectId)
        : [];
      const previousHotspotObjects = queryClient.getQueryData(['hotspotObjects', hotspotId]);
      if (projectId) {
        setIronview360PhotoHotspotQueries(
          queryClient,
          projectId,
          (hotspots) =>
            hotspots?.map((hotspot) =>
              Number(hotspot.id) === hotspotId
                ? { ...hotspot, ...data }
                : hotspot,
            ),
        );
      }

      return { previousPhotoHotspots, previousHotspotObjects };
    },
    onError: (err, variables, context) => {
      console.error('Error updating hotspot:', err);
      if (context?.previousPhotoHotspots) {
        context.previousPhotoHotspots.forEach(([queryKey, data]) => queryClient.setQueryData(queryKey, data));
      }
      if (context?.previousHotspotObjects) {
        queryClient.setQueryData(['hotspotObjects', variables.hotspotId], context.previousHotspotObjects);
      }
    },
    onSettled: async (_, error, variables) => {
      if (projectId) {
        await invalidateIronview360PhotoHotspots(queryClient);
      }
      if (error) {
        queryClient.invalidateQueries({ queryKey: ['hotspotObjects', variables.hotspotId] });
      }
      invalidateLayoutHotspots(queryClient);
    },
  });
};

export const useDeleteHotspot = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ hotspotId, projectId }: { hotspotId: number; projectId: string }) =>
      viewerMutationOptions
        .deleteHotspot(ironview360ViewerAdapter)
        .mutationFn({
          context: createIronview360ViewerContext(projectId),
          hotspotId,
        }),
    onMutate: async ({ hotspotId, projectId }) => {
      await cancelIronview360PhotoHotspots(queryClient, projectId);
      const previousPhotoHotspots =
        getIronview360PhotoHotspotQueries(queryClient, projectId);
      setIronview360PhotoHotspotQueries(
        queryClient,
        projectId,
        (hotspots) =>
          hotspots?.filter(
            (hotspot) => Number(hotspot.id) !== hotspotId,
          ),
      );

      return { previousPhotoHotspots };
    },
    onError: (err, _variables, context) => {
      console.error('Error deleting hotspot:', err);
      if (context?.previousPhotoHotspots) {
        context.previousPhotoHotspots.forEach(([queryKey, data]) => queryClient.setQueryData(queryKey, data));
      }
    },
    onSettled: async (_, _error, { hotspotId }) => {
      await invalidateIronview360PhotoHotspots(queryClient);
      queryClient.removeQueries({ queryKey: ['hotspotObjects', hotspotId] });
      queryClient.removeQueries({ queryKey: ['hotspotOverlays', hotspotId] });
      queryClient.removeQueries({ queryKey: ['notes', hotspotId] });
      queryClient.removeQueries({ queryKey: ['maintenanceRequests', hotspotId] });
      invalidateLayoutHotspots(queryClient);
    },
  });
};

export const useProjectUsers = (projectId: string | undefined) => {
  return useQuery<ProjectUser[]>({
    queryKey: ['projectUsers', projectId],
    queryFn: () => getProjectUsers(projectId!),
    enabled: !!projectId,
    staleTime: 1000 * 60 * 10,
    gcTime: 1000 * 60 * 60,
    refetchOnWindowFocus: false,
  });
};

export const useHotspotObjects = (hotspotId: number | undefined, _projectId: string | undefined) => {
  const overlaysQuery = useQuery<HotspotOverlaysResponse>({
    queryKey: ['hotspotOverlays', hotspotId],
    queryFn: () => getHotspotOverlays(hotspotId!),
    enabled: hotspotId !== undefined,
    staleTime: 1000 * 60 * 5,
    gcTime: 1000 * 60 * 30,
    refetchOnWindowFocus: false,
  });

  const allObjects = React.useMemo(() => {
    const objects: Array<{
      id: number | string;
      type: 'note' | 'image' | 'label';
      title: string;
      created_at: string;
      updated_at: string;
      rawData?: HotspotOverlay;
    }> = [];

    if (overlaysQuery.data?.overlays) {
      overlaysQuery.data.overlays.forEach((overlay: HotspotOverlay) => {
        const overlayWithDate = overlay as HotspotOverlay & { created_at?: string };
        objects.push({
          id: overlay.id,
          type: 'image',
          title: overlay.title || 'Image',
          created_at: overlayWithDate.created_at || new Date().toISOString(),
          updated_at: overlayWithDate.created_at || new Date().toISOString(),
          rawData: overlay,
        });
      });
    }

    return objects.sort(
      (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
    );
  }, [overlaysQuery.data]);

  return {
    objects: allObjects,
    isLoading: overlaysQuery.isLoading,
    error: overlaysQuery.error,
    refetch: useCallback(() => {
      if (overlaysQuery.isStale) overlaysQuery.refetch();
    }, [overlaysQuery.refetch, overlaysQuery.isStale]),
  };
};
